import { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { TextField } from "layouts/Flow Designer/components/ComponentActionsGenerator/components/StyledComponents";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PERMISSIONS } from "constants/portal";
import { permissionChecker } from "helpers/common";
import { createSharedFlowConfiguration, deleteSharedFlowConfiguration, getSharedFlowConfigurations, updateFlow, updateSharedFlowConfiguration } from "api/FlowDesignerApis";
import { setSavedFlow, setSharedFlowConfigurations } from "redux/actions/flowDesigner/flowsActionCreator";
import { clearToolboxSelectedMenuItem, closeToolboxDetails } from "redux/actions/flowDesigner/settingsActionCreator";
import ConfigurationFields from "../components/ConfigurationFields";
import {
   CONFIGURATION_KEY_PATTERN,
   RESERVED_CONFIGURATION_KEYS,
   cloneConfiguration,
   configurationToFields,
   createConfigurationField,
   fieldsToConfiguration,
   getFlowSettings,
   getTransmittableFlow,
   isConfigurationFieldInvalid,
   toConfigurationKey
} from "../helpers";
import { cacheUpdateSocketIoClient } from "App";

const HEADER_ACTION_SX = { minWidth: "180px", minHeight: "40.8px", height: "40.8px", maxHeight: "40.8px" };
const FULL_WIDTH_INPUT_SX = {
   width: "100%",
   "& .MuiInputBase-root, & .MuiInputBase-input": {
      width: "100% !important",
      minWidth: 0,
      maxWidth: "none"
   }
};
const StyledStack = styled(Stack)(({ theme }) => ({
   maxHeight: "420px",
   overflowY: "auto",
   boxSizing: "border-box",
   padding: theme.spacing(0.5, 0, 0.5, 0)
}));

const SharedFlowConfiguration = ({ currentSavedFlow, allowEditWorkflow }) => {
   const dispatch = useDispatch();
   const { user } = useAuth0();
   const { userPermissions } = useContext(PermissionsContext);
   const { sharedFlowConfigurations } = useSelector((state) => state.designer.flows);
   const [selectedKey, setSelectedKey] = useState(null);
   const [name, setName] = useState("");
   const [configurationKey, setConfigurationKey] = useState("");
   const [fields, setFields] = useState([]);
   const [isCreating, setIsCreating] = useState(false);
   const [isKeyGenerated, setIsKeyGenerated] = useState(true);
   const [submitting, setSubmitting] = useState(false);
   const [initialEditorState, setInitialEditorState] = useState(null);
   const [configurationToDelete, setConfigurationToDelete] = useState(null);
   const [deleting, setDeleting] = useState(false);
   const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
   const [availableConfigurations, setAvailableConfigurations] = useState({});
   const [selectedConfigurationKeys, setSelectedConfigurationKeys] = useState([]);
   const [loadingConfigurations, setLoadingConfigurations] = useState(false);
   const [savingSelection, setSavingSelection] = useState(false);
   const canManage = Boolean(permissionChecker(userPermissions, PERMISSIONS.EDIT_ANY_WORKFLOWS));
   const selectedConfiguration = selectedKey ? sharedFlowConfigurations?.[selectedKey] : null;
   const hasInvalidFields = useMemo(() => fields.some((field) => isConfigurationFieldInvalid(field, { protectReservedKeys: true })), [fields]);
   const hasEditorChanges = useMemo(() => initialEditorState !== null && JSON.stringify({ name, configurationKey, fields }) !== JSON.stringify(initialEditorState), [configurationKey, fields, initialEditorState, name]);

   const handleClose = () => {
      dispatch(closeToolboxDetails());
      dispatch(clearToolboxSelectedMenuItem());
   };

   const resetEditor = () => {
      setSelectedKey(null);
      setName("");
      setConfigurationKey("");
      setFields([]);
      setIsCreating(false);
      setIsKeyGenerated(true);
      setInitialEditorState(null);
   };

   const getSavedConfigurationKeys = () => {
      const keys = getFlowSettings(currentSavedFlow).sharedFlowConfigurationKeys;
      return Array.isArray(keys) ? [...new Set(keys)] : [];
   };

   const saveSharedFlowConfigurationSelection = async (configurationKeys, configurations = availableConfigurations) => {
      const newReferencedKeys = new Set(configurationKeys),
         hasSharedFlowConfigurations = newReferencedKeys.size > 0,
         uniqueKeys = [...newReferencedKeys],
         currentSettings = getFlowSettings(currentSavedFlow),
         previouslyReferencedKeys = new Set(currentSettings.sharedFlowConfigurationKeys),
         hadSharedFlowConfigurations = currentSettings.hasSharedFlowConfigurations,
         updatedFlowSettings = {
            ...currentSettings,
            hasSharedFlowConfigurations,
            sharedFlowConfigurationKeys: uniqueKeys
         };

      if (hadSharedFlowConfigurations !== hasSharedFlowConfigurations || newReferencedKeys.symmetricDifference(previouslyReferencedKeys).size)
         if (!(await updateFlow(currentSavedFlow.tenant_id, currentSavedFlow.id, getTransmittableFlow(currentSavedFlow, updatedFlowSettings), false)))
            return false;

      const selectedConfigurations = uniqueKeys.reduce((result, key) => {
         const configuration = configurations[key] ?? sharedFlowConfigurations?.[key];
         if (configuration) result[key] = configuration;
         return result;
      }, {});

      dispatch(setSavedFlow({ flow_settings: JSON.stringify(updatedFlowSettings) }));
      dispatch(setSharedFlowConfigurations(selectedConfigurations));
      return true;
   };

   const openSelectionDialog = async () => {
      setSelectionDialogOpen(true);
      setSelectedConfigurationKeys(getSavedConfigurationKeys());
      setAvailableConfigurations({});
      setLoadingConfigurations(true);

      const response = await getSharedFlowConfigurations(currentSavedFlow.tenant_id);
      setLoadingConfigurations(false);

      if (!response?.isSuccessful) {
         toast.error(response?.message || "Failed to load shared configurations.");
         return;
      }

      const configurations = response.resultObject ?? {};
      setAvailableConfigurations(configurations);
      setSelectedConfigurationKeys((current) => current.filter((key) => configurations[key]));
   };

   const saveSelection = async () => {
      if (savingSelection) return;

      setSavingSelection(true);
      const saved = await saveSharedFlowConfigurationSelection(selectedConfigurationKeys);
      setSavingSelection(false);

      if (!saved) {
         toast.error("The shared configuration selection could not be saved.");
         return;
      }

      setSelectionDialogOpen(false);
      toast.success("Shared configuration selection saved successfully.");
   };

   const openConfiguration = (key) => {
      const configuration = sharedFlowConfigurations[key];
      const configurationFields = configurationToFields(configuration.values);
      setSelectedKey(key);
      setName(configuration.name || key);
      setConfigurationKey(key);
      setFields(configurationFields);
      setIsCreating(false);
      setIsKeyGenerated(false);
      setInitialEditorState({ name: configuration.name || key, configurationKey: key, fields: configurationFields });
   };

   const startCreate = () => {
      setSelectedKey(null);
      setName("");
      setConfigurationKey("");
      setFields([]);
      setIsCreating(true);
      setIsKeyGenerated(true);
      setInitialEditorState({ name: "", configurationKey: "", fields: [] });
   };

   const updateField = (index, property, value) => {
      setFields((current) =>
         current.map((field, fieldIndex) => {
            if (fieldIndex !== index) return field;
            if (property !== "type") return { ...field, [property]: value };

            const nextValue = value === "boolean" ? true : value === "object" ? "{}" : "";
            return { ...field, type: value, value: nextValue };
         })
      );
   };

   const updateSharedFlowConfigurations = (configurationKeyToUpdate, configuration) => {
      const nextConfigurations = {
         ...(sharedFlowConfigurations || {}),
         [configurationKeyToUpdate]: configuration
      };
      dispatch(setSharedFlowConfigurations(nextConfigurations));
   };

   const handleSave = async () => {
      const normalizedKey = configurationKey.trim().toLowerCase();
      if (!name.trim()) {
         toast.error("A configuration name is required.");
         return;
      }
      if (!CONFIGURATION_KEY_PATTERN.test(normalizedKey)) {
         toast.error("The context key must start with a lowercase letter and contain only lowercase letters, numbers, and underscores.");
         return;
      }
      if (RESERVED_CONFIGURATION_KEYS.has(normalizedKey)) {
         toast.error("This context key is reserved. Choose a different context key.");
         return;
      }
      if (hasInvalidFields) {
         toast.error("Every configuration field needs a key and a valid value for its type.");
         return;
      }

      let values;
      try {
         values = fieldsToConfiguration(fields);
      } catch {
         toast.error("Object values must be valid JSON objects.");
         return;
      }

      setSubmitting(true);
      const input = {
         configuration_key: normalizedKey,
         name: name.trim(),
         values
      };
      const response = isCreating ? await createSharedFlowConfiguration(currentSavedFlow.tenant_id, user.cxfUserId, input) : await updateSharedFlowConfiguration(currentSavedFlow.tenant_id, user.cxfUserId, selectedKey, input);

      if (!response?.isSuccessful) {
         setSubmitting(false);
         toast.error(response?.message || "Failed to save shared flow configuration.");
         return;
      }

      const savedConfiguration = response.resultObject;
      const savedKey = isCreating ? normalizedKey : selectedKey;
      const normalizedConfiguration = {
         name: savedConfiguration.name,
         values: savedConfiguration.values,
         createdBy: savedConfiguration.createdBy,
         createdAt: savedConfiguration.createdAt,
         updatedBy: savedConfiguration.updatedBy,
         updatedAt: savedConfiguration.updatedAt
      };

      if (isCreating) {
         const nextConfigurations = {
            ...(sharedFlowConfigurations || {}),
            [savedKey]: normalizedConfiguration
         };
         const selectionSaved = await saveSharedFlowConfigurationSelection([...getSavedConfigurationKeys(), savedKey], nextConfigurations);

         if (!selectionSaved) {
            setSubmitting(false);
            toast.error("The configuration was created, but it could not be selected for this flow.");
            resetEditor();
            return;
         }
      } else {
         updateSharedFlowConfigurations(savedKey, normalizedConfiguration);

         if (cacheUpdateSocketIoClient?.connected) {
            cacheUpdateSocketIoClient.emit("sharedFlowConfigurationUpdate", { tenantId: currentSavedFlow.tenant_id, configurationName: selectedKey });
            console.debug(`Requested shared flow configuration cache update in cxf-executor for configuration ${selectedKey}) for tenant ${currentSavedFlow.tenant_id}`);
         } else {
            toast.error('Unable to request shared flow configuration cache update in cxf-executor because the service is not running or not reachable ');
            return;
         }
      }

      setSubmitting(false);
      resetEditor();
      toast.success(response.message);
   };

   const handleDelete = async () => {
      if (!configurationToDelete) return;

      setDeleting(true);
      const response = await deleteSharedFlowConfiguration(currentSavedFlow.tenant_id, user.cxfUserId, configurationToDelete);
      setDeleting(false);
      if (!response?.isSuccessful) {
         toast.error(response?.message || "Failed to delete shared flow configuration.");
         return;
      }

      const nextConfigurations = cloneConfiguration(sharedFlowConfigurations || {});
      delete nextConfigurations[configurationToDelete];
      const remainingKeys = getSavedConfigurationKeys().filter((key) => key !== configurationToDelete);
      const selectionSaved = await saveSharedFlowConfigurationSelection(remainingKeys, nextConfigurations);

      if (!selectionSaved) {
         dispatch(setSharedFlowConfigurations(nextConfigurations));
         toast.error("The configuration was deleted, but this flow's selection could not be updated.");
      } else {
         toast.success(response.message);
      }
      if (selectedKey === configurationToDelete) resetEditor();
      setConfigurationToDelete(null);
   };

   if (!selectedConfiguration && !isCreating) {
      return (
         <>
            <Box sx={{ mt: 2, pb: 2, display: "flex", flex: 1, minHeight: 0, flexDirection: "column" }}>
               <SoftBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <SoftTypography variant="button" fontWeight="medium">
                     Selected shared configurations
                  </SoftTypography>
                  <SoftBox sx={{ display: "flex", gap: 1 }}>
                     <SoftButton sx={HEADER_ACTION_SX} variant="outlined" color="info" onClick={openSelectionDialog} disabled={!allowEditWorkflow}>
                        Select configurations
                     </SoftButton>
                     <SoftButton sx={HEADER_ACTION_SX} variant="contained" color="primary" onClick={startCreate} disabled={!canManage}>
                        Create configuration
                     </SoftButton>
                  </SoftBox>
               </SoftBox>
               <StyledStack spacing={1}>
                  {Object.entries(sharedFlowConfigurations || {}).map(([key, configuration]) => (
                     <SoftBox key={key} sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                        <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                           <SoftTypography variant="button" fontWeight="bold">
                              {configuration.name || key}
                           </SoftTypography>
                           <SoftTypography variant="caption" display="block" color="text">{`@shared_flow_configurations.${key}`}</SoftTypography>
                        </SoftBox>
                        <SoftButton variant="outlined" color="info" onClick={() => openConfiguration(key)}>
                           Open
                        </SoftButton>
                     </SoftBox>
                  ))}
                  {!Object.keys(sharedFlowConfigurations || {}).length && (
                     <SoftTypography variant="caption" color="text">
                        No shared configurations are selected for this flow.
                     </SoftTypography>
                  )}
               </StyledStack>
               <SoftBox sx={{ display: "flex", justifyContent: "flex-end", mt: "auto", pt: 2, pr: 1 }}>
                  <SoftButton sx={{ minWidth: "110px", m: 0 }} variant="outlined" color="info" onClick={handleClose}>
                     Close
                  </SoftButton>
               </SoftBox>
            </Box>
            <Dialog
               open={selectionDialogOpen}
               onClose={() => {
                  if (!savingSelection) setSelectionDialogOpen(false);
               }}
               fullWidth
               maxWidth="sm"
               aria-labelledby="selectSharedConfigurationsDialogTitle"
            >
               <DialogTitle id="selectSharedConfigurationsDialogTitle">Select shared configurations</DialogTitle>
               <DialogContent>
                  {loadingConfigurations ? (
                     <SoftBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                     </SoftBox>
                  ) : (
                     <Stack spacing={1.5} sx={{ py: 1 }}>
                        {Object.entries(availableConfigurations).map(([key, configuration]) => (
                           <SoftBox
                              key={key}
                              component="label"
                              sx={{
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "space-between",
                                 gap: 3,
                                 width: "100%",
                                 px: 2,
                                 py: 1.5,
                                 borderRadius: 1,
                                 cursor: "pointer",
                                 transition: "background-color 0.2s ease",
                                 "&:hover": { backgroundColor: "action.hover" }
                              }}
                           >
                              <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                                 <SoftTypography variant="button" fontWeight="medium" display="block">
                                    {configuration.name || key}
                                 </SoftTypography>
                                 <SoftTypography variant="caption" display="block" color="text" sx={{ mt: 0.5 }}>
                                    {`@shared_flow_configurations.${key}`}
                                 </SoftTypography>
                              </SoftBox>
                              <Checkbox
                                 checked={selectedConfigurationKeys.includes(key)}
                                 inputProps={{ "aria-label": `Select ${configuration.name || key}` }}
                                 sx={{ flexShrink: 0, m: 0 }}
                                 onChange={(event) => {
                                    setSelectedConfigurationKeys((current) => (event.target.checked ? [...new Set([...current, key])] : current.filter((configurationKey) => configurationKey !== key)));
                                 }}
                              />
                           </SoftBox>
                        ))}
                        {!Object.keys(availableConfigurations).length && (
                           <SoftTypography variant="caption" color="text">
                              No shared configurations have been created for this tenant.
                           </SoftTypography>
                        )}
                     </Stack>
                  )}
               </DialogContent>
               <DialogActions>
                  <SoftButton variant="outlined" color="primary" disabled={savingSelection} onClick={() => setSelectionDialogOpen(false)}>
                     Cancel
                  </SoftButton>
                  {Object.keys(availableConfigurations).length > 0 && (
                     <SoftButton color="primary" disabled={loadingConfigurations || savingSelection} onClick={saveSelection}>
                        Save
                     </SoftButton>
                  )}
               </DialogActions>
            </Dialog>
         </>
      );
   }

   return (
      <Box sx={{ mt: 2, pb: 2, px: 0.5, display: "flex", flex: 1, minHeight: 0, flexDirection: "column" }}>
         <SoftBox sx={{ display: "flex", flexShrink: 0, justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <SoftTypography variant="button" fontWeight="medium">
               {isCreating ? "Create shared configuration" : "Edit shared configuration"}
            </SoftTypography>
            <SoftButton sx={HEADER_ACTION_SX} variant="outlined" color="info" onClick={resetEditor}>
               Back
            </SoftButton>
         </SoftBox>
         <Box sx={{ flex: 1, minHeight: 0, overflowX: "hidden", overflowY: "auto", px: 0.5 }}>
            <Stack spacing={2} sx={{ mb: 2 }}>
               <TextField
                  sx={FULL_WIDTH_INPUT_SX}
                  label="Name"
                  required
                  value={name}
                  disabled={!canManage}
                  onChange={(event) => {
                     const nextName = event.target.value;
                     setName(nextName);
                     if (isCreating && isKeyGenerated) setConfigurationKey(toConfigurationKey(nextName));
                  }}
               />
               <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                     <TextField
                        sx={FULL_WIDTH_INPUT_SX}
                        label="Context key"
                        required
                        value={configurationKey}
                        disabled={!isCreating || !canManage || isKeyGenerated}
                        helperText={isCreating ? "Used in @shared_flow_configurations.<key>. This cannot be changed after creation." : `@shared_flow_configurations.${configurationKey}`}
                        onChange={(event) => setConfigurationKey(toConfigurationKey(event.target.value))}
                     />
                  </SoftBox>
                  {isCreating && (
                     <SoftBox sx={{ width: "160px", flexShrink: 0 }}>
                        <SoftTypography display="block" sx={{ mb: 0.5, fontSize: "13px", fontWeight: 400 }}>
                           Generate from name
                        </SoftTypography>
                        <Switch
                           checked={isKeyGenerated}
                           disabled={!canManage}
                           onChange={(event) => {
                              const shouldGenerate = event.target.checked;
                              setIsKeyGenerated(shouldGenerate);
                              if (shouldGenerate) setConfigurationKey(toConfigurationKey(name));
                           }}
                        />
                     </SoftBox>
                  )}
               </SoftBox>
            </Stack>
            <Stack spacing={1.5}>
               <ConfigurationFields fields={fields} disabled={!canManage} onChange={updateField} onRemove={(index) => setFields((current) => current.filter((_, fieldIndex) => fieldIndex !== index))} />
            </Stack>
            {canManage && (
               <SoftBox sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <SoftButton variant="outlined" color="info" onClick={() => setFields((current) => [...current, createConfigurationField()])}>
                     Add Field
                  </SoftButton>
               </SoftBox>
            )}
         </Box>
         <SoftBox sx={{ display: "flex", flexShrink: 0, justifyContent: isCreating || !canManage ? "flex-end" : "space-between", mt: 2 }}>
            {!isCreating && canManage && (
               <SoftButton color="error" sx={{ backgroundColor: "#E53E3E !important" }} disabled={submitting || deleting} onClick={() => setConfigurationToDelete(selectedKey)}>
                  Delete configuration
               </SoftButton>
            )}
            {canManage && (
               <SoftButton variant="contained" color="primary" disabled={submitting || !hasEditorChanges} onClick={handleSave}>
                  Save
               </SoftButton>
            )}
         </SoftBox>
         <Dialog
            open={Boolean(configurationToDelete)}
            onClose={() => {
               if (!deleting) setConfigurationToDelete(null);
            }}
            aria-labelledby="deleteSharedConfigurationDialogTitle"
            aria-describedby="deleteSharedConfigurationDialogDescription"
         >
            <DialogTitle id="deleteSharedConfigurationDialogTitle">Delete shared configuration</DialogTitle>
            <DialogContent>
               <DialogContentText id="deleteSharedConfigurationDialogDescription">
                  Delete {sharedFlowConfigurations?.[configurationToDelete]?.name || configurationToDelete}? Existing flow references to @shared_flow_configurations.{configurationToDelete} will no longer resolve.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <SoftButton variant="outlined" color="primary" disabled={deleting} onClick={() => setConfigurationToDelete(null)}>
                  Cancel
               </SoftButton>
               <SoftButton color="error" sx={{ backgroundColor: "#E53E3E !important" }} disabled={deleting} onClick={handleDelete}>
                  Delete
               </SoftButton>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default SharedFlowConfiguration;
